/**
 * Shared roster mutations — writes both brand and admin can perform.
 * Routes are unified /api/v1/roster/* — BE enforces role-gating per handler.
 */
import { apiClient } from '@discovr/common';
import { postThreadComment } from './shared';

const approveCreator = (rosterEntryId: string, payload?: { selected_deliverables?: string[] }): Promise<void> =>
  apiClient
    .post(`/roster/${encodeURIComponent(rosterEntryId)}/approve`, payload ?? {})
    .then(() => undefined);

/** Per-outcome counts returned by the bulk approve-all endpoint. */
export interface BulkApproveResult {
  approved_count: number;
  skipped_count: number;
  failed_count: number;
}

/**
 * Bulk "approve all eligible" creators on a campaign. The route is unified; the
 * BE dispatches by role — brand approves pending creators that have a main
 * deliverable selected (others are skipped), admin advances every
 * brand-accepted creator. Returns per-outcome counts.
 */
const approveAllCreators = (campaignId: string): Promise<BulkApproveResult> =>
  apiClient
    .post<{ data: BulkApproveResult }>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/approve-all`,
      {},
    )
    .then((res) => res.data.data);

const rejectCreator = (rosterEntryId: string, reason: string): Promise<void> =>
  apiClient
    .post(`/roster/${encodeURIComponent(rosterEntryId)}/reject`, { reason })
    .then(() => undefined);

const selectDeliverable = (rosterEntryId: string, types: string[]): Promise<void> =>
  apiClient
    .put(`/roster/${encodeURIComponent(rosterEntryId)}/deliverables`, {
      selected_deliverables: types,
    })
    .then(() => undefined);

/**
 * PATCH /roster/:id/products — full-replace a confirmed creator's assigned
 * products (Multi-Product Campaigns). Admin and brand writers. Empty ids
 * unassigns all. The response's assigned_products is ignored; the roster refetch
 * carries the resolved products.
 */
const assignProducts = (rosterEntryId: string, productIds: string[]): Promise<void> =>
  apiClient
    .patch(`/roster/${encodeURIComponent(rosterEntryId)}/products`, {
      product_ids: productIds,
    })
    .then(() => undefined);

/** POST /roster/:id/products/keep-previous — restores the products a rollback set aside (204). */
const keepPreviousProducts = (rosterEntryId: string): Promise<void> =>
  apiClient.post(`/roster/${encodeURIComponent(rosterEntryId)}/products/keep-previous`).then(() => undefined);

const postScriptThreadComment = (rosterEntryId: string, body: string): Promise<void> =>
  postThreadComment(rosterEntryId, 'script', body).then(() => undefined);

const postContentThreadComment = (rosterEntryId: string, body: string): Promise<void> =>
  postThreadComment(rosterEntryId, 'content', body).then(() => undefined);

export function createRosterMutationsApi() {
  return {
    approveCreator,
    approveAllCreators,
    rejectCreator,
    selectDeliverable,
    assignProducts,
    keepPreviousProducts,
    postScriptThreadComment,
    postContentThreadComment,
  };
}

export type RosterMutationsApi = ReturnType<typeof createRosterMutationsApi>;
